import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AlertTriangle, X } from "lucide-react";

/* Shared ticket modal used both by customers (CustomerOrderDetail) and
   by vendors (OrderDrawer "Escalate to admin"). The caller passes:
     - kind: "customer_issue" | "vendor_escalation"
     - orderId: optional, stamped onto the ticket
     - vendorId: optional, stamped onto the ticket for vendor escalations
     - title / cta / placeholder / topic: tone customization
*/
export default function ReportProblemModal({ open, onClose, kind, orderId, vendorId, title, cta, placeholder, topic = "order", onSent }) {
  const { user } = useAuth();
  const [message, setMessage] = useState("");
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ticketId, setTicketId] = useState(null);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/support/contact", {
        name, email, topic, kind,
        order_id: orderId || null,
        vendor_id: vendorId || null,
        message,
      });
      setTicketId(data.ticket_id);
      onSent?.(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not send — please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="report-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b flex items-start justify-between">
          <div>
            <p className="overline">{kind === "vendor_escalation" ? "Vendor escalation" : "Report a problem"}</p>
            <h3 className="font-display text-2xl mt-1">{title || "Tell us what went wrong"}</h3>
            {orderId && <p className="text-xs text-[var(--ff-text-muted)] mt-1">Linked to order <span className="font-mono">#{orderId.slice(0,12)}</span></p>}
          </div>
          <button onClick={onClose} className="text-[var(--ff-text-muted)] hover:text-[var(--ff-text-primary)]" aria-label="Close" data-testid="report-modal-close">
            <X size={20}/>
          </button>
        </div>

        {ticketId ? (
          <div className="p-6 space-y-3" data-testid="report-success">
            <div className="bg-emerald-50 border border-emerald-200 p-4 rounded text-sm text-emerald-800">
              Ticket <strong className="font-mono">{ticketId}</strong> opened. Our team will reply within one business day.
            </div>
            <button onClick={onClose} className="btn-primary w-full" data-testid="report-done">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            {!user && (
              <div className="grid md:grid-cols-2 gap-3">
                <label className="block">
                  <span className="overline">Your name</span>
                  <input required className="input-base mt-2" value={name} onChange={e => setName(e.target.value)} data-testid="report-name"/>
                </label>
                <label className="block">
                  <span className="overline">Email</span>
                  <input required type="email" className="input-base mt-2" value={email} onChange={e => setEmail(e.target.value)} data-testid="report-email"/>
                </label>
              </div>
            )}
            <label className="block">
              <span className="overline">{kind === "vendor_escalation" ? "What needs admin attention?" : "What happened?"}</span>
              <textarea required minLength={10} rows={6} className="input-base mt-2"
                placeholder={placeholder || "Be specific — include item names, photos in your reply email, and any tracking info you have."}
                value={message} onChange={e => setMessage(e.target.value)} data-testid="report-message"/>
            </label>
            {error && (
              <div className="bg-rose-50 border border-rose-200 p-3 rounded text-sm text-rose-800 flex gap-2" data-testid="report-error">
                <AlertTriangle size={16}/> {error}
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
              <button type="submit" disabled={busy} className="btn-primary" data-testid="report-submit">
                {busy ? "Sending…" : (cta || "Open ticket")}
              </button>
            </div>
            <p className="text-[11px] text-[var(--ff-text-muted)]">
              By submitting you agree to our <a href="/terms" className="underline" target="_blank" rel="noreferrer">Terms</a> and <a href="/privacy" className="underline" target="_blank" rel="noreferrer">Privacy Policy</a>.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
